import React from "react"

import TopicLayout from "../shared/TopicLayout"
import {Arrow, Box, Diagram} from "../shared/Diagram"

export default function MultiRegionDeployment() {
    return (
        <TopicLayout
            title="Active-active vs. active-passive"
            intro="Whether every region serves live traffic and must reconcile concurrent writes, or one region is a standby that only takes over when the primary fails."
            diagram={
                <Diagram viewBox="0 0 560 200">
                    <text x={140} y={14} textAnchor="middle" fontSize={12} opacity={0.8}>Active-active — both serve traffic</text>
                    <Box x={20} y={40} width={70} height={32} label="Users"/>
                    <Box x={120} y={40} width={90} height={32} label="Region A" accent/>
                    <Box x={20} y={110} width={70} height={32} label="Users"/>
                    <Box x={120} y={110} width={90} height={32} label="Region B" accent/>
                    <Arrow x1={90} y1={56} x2={120} y2={56}/>
                    <Arrow x1={90} y1={126} x2={120} y2={126}/>
                    <Arrow x1={210} y1={62} x2={210} y2={110} muted/>
                    <Arrow x1={220} y1={110} x2={220} y2={62} muted/>
                    <text x={140} y={168} textAnchor="middle" fontSize={11} opacity={0.7}>needs conflict resolution for concurrent writes</text>

                    <line x1={280} y1={10} x2={280} y2={190} stroke="var(--header-background)" opacity={0.25}/>

                    <text x={420} y={14} textAnchor="middle" fontSize={12} opacity={0.8}>Active-passive — standby takes over</text>
                    <Box x={300} y={78} width={70} height={32} label="Users"/>
                    <Box x={400} y={40} width={110} height={32} label="Region A" sublabel="active" accent/>
                    <Box x={400} y={120} width={110} height={32} label="Region B" sublabel="standby"/>
                    <Arrow x1={370} y1={90} x2={400} y2={56}/>
                    <Arrow x1={455} y1={72} x2={455} y2={120} label="replicate" dashed muted/>
                    <Arrow x1={370} y1={98} x2={400} y2={130} dashed/>
                    <text x={375} y={114} fontSize={11} fill="var(--p-color)">failover</text>
                    <text x={420} y={168} textAnchor="middle" fontSize={11} opacity={0.7}>only one region takes writes at a time</text>
                </Diagram>
            }
            useWhen={[
                'Active-active: read/write latency for geographically distributed users matters more than avoiding write conflicts, and the data model can tolerate or resolve concurrent updates.',
                'Active-passive: simplicity and a single source of truth matter more than multi-region write latency, and an occasional failover pause is acceptable.'
            ]}
            tradeOff="Active-active gives every user a nearby region for both reads and writes and survives a full regional outage with no failover step, but concurrent writes to the same record from different regions need a conflict-resolution strategy (last-write-wins, CRDTs, or application-level merge logic). Active-passive keeps the data model simple — there is only ever one writer — but the standby region is idle capacity, and failover is an operational event with its own risk: promoting the wrong replica, or promoting before replication has caught up, can lose data."
            ruleOfThumb="Start active-passive with tested, automated failover; move to active-active only for the specific data that needs multi-region write latency, and design conflict resolution for that data explicitly rather than bolting it on later."
        />
    )
}
